/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import CountryCode from '@linc-technologies/linced-common/enums/country-code';
import config from 'linced-admin/config/environment';

export default class TemplatesController extends Controller {
  // Services
  @service
  fetch;

  @service
  router;

  @service
  store;

  @service
  toaster;

  @tracked
  editingTemplate = null;

  @tracked
  templateDialogOpen = false;

  @tracked
  templateToApply = null;

  @tracked
  applyErrorMessage = '';

  @tracked
  applyInProgress = false;

  @tracked
  applyDialogOpen = false;

  @tracked
  selectedSchool = null;

  @action
  setSelectedSchool(value) {
    this.selectedSchool = value;
  }

  @tracked
  deleteInProgress;

  @tracked
  deleteTemplateDialogOpen;

  @tracked
  errorMessage;

  @tracked
  templateId;

  @tracked
  templateLabel;

  @action
  addTemplate() {
    let template = this.store.createRecord('enrolments/schema', {
      primaryContacts: {
        count: 3,
        index: 1,
        title: 'Primary Contact(s)',
      },
      emergencyContacts: {
        count: 2,
        index: 2,
        title: 'Emergency Contact(s)',
        description:
          'Who should the school contact in case of an emergency, if the Primary Contacts are unreachable?',
      },
      medicalContacts: {
        count: 2,
        index: 3,
        title: 'Medical Contact(s)',
      },
      medicalConditions: {
        count: 2,
        index: 4,
        title: 'Medical Condition(s)',
      },
    });
    this.editingTemplate = template;
    this.templateDialogOpen = true;
  }

  @action
  editTemplate(template) {
    this.editingTemplate = template;
    this.templateDialogOpen = true;
  }

  @action
  closeTemplateDialog() {
    this.editingTemplate.rollbackAttributes();
    this.templateDialogOpen = false;
  }

  @action
  saveTemplate() {
    let isNew = isEmpty(this.editingTemplate.id);

    this.editingTemplate
      .save()
      .then(() => {
        this.toaster.success('Enrolment form template saved successfully.');
        this.templateDialogOpen = false;
        if (isNew) {
          // This is a new template - go straight to editing the form itself.
          this.router.transitionTo(
            'enrolment.schema.template',
            this.editingTemplate.id,
          );
        }
      })
      .catch(() => {
        this.toaster.error('Error saving enrolment form template!');
      });
  }

  @action
  deleteTemplate(template) {
    this.errorMessage = '';
    this.templateLabel = template.get('templateName');
    this.templateId = template.id;
    this.deleteTemplateDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    let toaster = this.toaster;

    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let template = this.store.peekRecord(
          'enrolments/schema',
          this.templateId,
        );

        // Delete record (locally + pushed to server).
        return template
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            template.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteTemplateDialogOpen = false;
        if (confirmed) {
          toaster.success('Template deleted successfully.');
        }
      })
      .catch(() => {
        toaster.error('Error deleting Template.');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // Apply templates
  @action
  openApplyDialog(template) {
    this.templateToApply = template;
    this.applyErrorMessage = '';
    this.applyInProgress = false;
    this.applyDialogOpen = true;
    this.selectedSchool = null;
  }

  @action
  closeApplyDialog() {
    this.applyDialogOpen = false;
  }

  @action
  applyTemplate() {
    let school = this.selectedSchool;
    if (isEmpty(school)) {
      return;
    }
    let data = {
      schoolId: school.id,
      countryCode: CountryCode.getLabel(school.countryCode),
      schoolCode: school.jurisdictionalId,
      schoolName: school.name,
      // TODO: We do not store an id on the enrolment schema so we can't do the
      //  blobby serving. This will work until we nuke the unauthenticated
      //  media routes.
      schoolLogoUrl: `${config.APP.apiHost}/media/${config.APP.apiPath}/media/${school.logoMedia}/logo.png}`,
      jurisdiction: school.jurisdiction,
    };
    let options = {
      data: JSON.stringify(data),
    };

    let templatesUri = `${config.APP.apiHost}/enrolments/${config.APP.apiPath}/templates/schemas`;
    let templateId = this.get('templateToApply.id');

    this.applyInProgress = true;

    this.fetch
      .post(`${templatesUri}/${templateId}/:apply`, options)
      .then(() => {
        this.toaster.success('Template applied successfully.');
        this.applyErrorMessage = '';
        this.applyInProgress = false;
        this.applyDialogOpen = false;
      })
      .catch((err) => {
        this.toaster.error(`Error applying template! ${err}`);
        this.applyErrorMessage = err.payload.message;
        this.applyInProgress = false;
      });
  }
}
